import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getAuthToken } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, PhoneCall, PhoneOff, Pause, Play, Mic, MicOff, UserPlus, Volume2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type Agent = { id: string; userId: string; status: string; name?: string; capacity?: number; activeCalls?: number };
type Call = {
  id: string; status: string; direction: string; callerName?: string; callerPhone?: string;
  queueName?: string; startedAt?: string; answeredAt?: string; endedAt?: string;
  notes?: string; disposition?: string; tags?: string[];
};

export default function AgentCallWorkspace() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [disposition, setDisposition] = useState("resolved");
  const [muted, setMuted] = useState(false);

  const me = useQuery<Agent>({ queryKey: ["/api/live-agents/me"] });
  const calls = useQuery<Call[]>({ queryKey: ["/api/calls"], refetchInterval: 5000 });
  const incoming = useMemo(() => (calls.data || []).filter(c => c.status === "ringing"), [calls.data]);
  const active = useMemo(() => (calls.data || []).find(c => c.id === activeId) || (calls.data || []).find(c => ["active", "on_hold"].includes(c.status)), [calls.data, activeId]);

  useEffect(() => { if (active) { setActiveId(active.id); setNotes(active.notes || ""); } }, [active?.id]);

  // WebSocket subscriptions: one socket per topic
  useEffect(() => {
    if (!user) return;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const token = getAuthToken();
    const base = `${proto}://${window.location.host}/ws/live-chat`;
    const topics = [`agent:${user.id}`, `calls:${(user as any).organizationId}`];
    const sockets = topics.map(topic => {
      const url = `${base}?topic=${encodeURIComponent(topic)}${token ? `&access_token=${encodeURIComponent(token)}` : ""}`;
      const ws = new WebSocket(url);
      ws.onmessage = () => { calls.refetch(); me.refetch(); };
      return ws;
    });
    return () => { sockets.forEach(s => { try { s.close(); } catch {} }); };
  }, [user?.id]);

  const setStatus = useMutation({
    mutationFn: async (status: string) =>
      apiRequest("PATCH", `/api/live-agents/${me.data?.id}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/live-agents/me"] }),
  });
  const answer = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/calls/${id}/answer`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/calls"] }),
  });
  const decline = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/calls/${id}/decline`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/calls"] }),
  });
  const hold = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/calls/${id}/hold`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/calls"] }),
  });
  const resume = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/calls/${id}/resume`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/calls"] }),
  });
  const muteCall = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/calls/${id}/${muted ? "unmute" : "mute"}`),
    onSuccess: () => setMuted(!muted),
  });
  const endCall = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/calls/${id}/end`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/calls"] }),
  });
  const wrapUp = useMutation({
    mutationFn: async () =>
      apiRequest("POST", `/api/calls/${active!.id}/wrap-up`, { notes, disposition }),
    onSuccess: () => {
      toast({ title: "Wrap-up complete", description: "Ticket created and call closed." });
      setActiveId(null); setNotes(""); setDisposition("resolved");
      queryClient.invalidateQueries({ queryKey: ["/api/calls"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
    },
  });

  const status = me.data?.status || "offline";
  const onCall = active && ["active", "on_hold"].includes(active.status);
  const inWrap = active && active.status === "wrap_up";

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="page-agent-workspace">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agent Workspace</h1>
          <p className="text-sm text-muted-foreground">Handle inbound and outbound calls.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={status === "available" ? "default" : "secondary"} data-testid="badge-agent-status">{status}</Badge>
          <Select value={status} onValueChange={(v) => setStatus.mutate(v)}>
            <SelectTrigger className="w-36" aria-label="Agent availability status" data-testid="select-agent-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="busy">Busy</SelectItem>
              <SelectItem value="away">Away</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_300px] gap-4">
        <Card data-testid="card-incoming">
          <CardHeader><CardTitle className="text-sm">Incoming ({incoming.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {incoming.length === 0 && <p className="text-xs text-muted-foreground">No ringing calls.</p>}
            {incoming.map(c => (
              <div key={c.id} className="border rounded-md p-3" data-testid={`row-incoming-${c.id}`}>
                <div className="text-sm font-medium truncate">{c.callerName || c.callerPhone}</div>
                <div className="text-xs text-muted-foreground">{c.queueName || "Default"}</div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" onClick={() => answer.mutate(c.id)} data-testid={`button-answer-${c.id}`}>
                    <PhoneCall className="w-3 h-3 mr-1" />Answer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decline.mutate(c.id)} data-testid={`button-decline-${c.id}`}>
                    <PhoneOff className="w-3 h-3 mr-1" />Decline
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="card-active-call">
          <CardHeader>
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Active Call</span>
              {active && <Badge variant="outline">{active.status}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!active && <div className="text-center py-12 text-muted-foreground"><Phone className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No active call</p></div>}
            {active && (
              <div className="space-y-3">
                <div>
                  <div className="text-lg font-semibold" data-testid="text-active-caller">{active.callerName || active.callerPhone}</div>
                  <div className="text-xs text-muted-foreground">{active.callerPhone}</div>
                </div>
                {onCall && (
                  <div className="flex flex-wrap gap-2">
                    {active.status === "active" ? (
                      <Button size="sm" variant="outline" onClick={() => hold.mutate(active.id)} data-testid="button-hold"><Pause className="w-3 h-3 mr-1" />Hold</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => resume.mutate(active.id)} data-testid="button-resume"><Play className="w-3 h-3 mr-1" />Resume</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => muteCall.mutate(active.id)} data-testid="button-mute">
                      {muted ? <Mic className="w-3 h-3 mr-1" /> : <MicOff className="w-3 h-3 mr-1" />}{muted ? "Unmute" : "Mute"}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => endCall.mutate(active.id)} data-testid="button-end-call"><PhoneOff className="w-3 h-3 mr-1" />End</Button>
                  </div>
                )}
                {inWrap && (
                  <div className="space-y-2 border-t pt-3">
                    <div className="text-xs font-medium">Wrap-up</div>
                    <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Call notes…" rows={3} aria-label="Call wrap-up notes" data-testid="input-call-notes" />
                    <Select value={disposition} onValueChange={setDisposition}>
                      <SelectTrigger aria-label="Call disposition" data-testid="select-disposition"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="follow_up">Follow up</SelectItem>
                        <SelectItem value="transferred">Transferred</SelectItem>
                        <SelectItem value="no_answer">No answer</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={() => wrapUp.mutate()} disabled={wrapUp.isPending} data-testid="button-wrap-up">
                      Save & Create Ticket
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="card-customer-info">
          <CardHeader><CardTitle className="text-sm">Customer</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-xs">
            {active ? (
              <>
                <div><span className="text-muted-foreground">Name:</span> {active.callerName || "—"}</div>
                <div><span className="text-muted-foreground">Phone:</span> {active.callerPhone || "—"}</div>
                <div><span className="text-muted-foreground">Queue:</span> {active.queueName || "—"}</div>
                <div><span className="text-muted-foreground">Started:</span> {active.startedAt && new Date(active.startedAt).toLocaleTimeString()}</div>
              </>
            ) : <p className="text-muted-foreground">Select an active call.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
